/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useMutation } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { useSystemMessages } from "../../hooks/useSystemMessages";
import { apolloClient } from "../../services/backend_gateway/apollo-client";
import {
  GetFeedbackImageUploadUrlDocument,
  SubmitFeedbackDocument,
} from "../../services/__generated__/graphql";
import { theme } from "../../styles/theme/theme";

// Client-side attachment limits. Kept in step with the server: the allowed
// types mirror getFeedbackImageUploadUrl's content-type allow-list and the size
// cap mirrors submitFeedback's MAX_IMAGE_BYTES. MAX_IMAGES follows the plan's
// "a small N" recommendation (the server accepts up to 5).
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_IMAGES = 3;

type Attachment = {
  id: number;
  file: File;
  previewUrl: string;
};

/**
 * Site-wide "Feedback" modal, opened from the left-rail icon. Submits a
 * free-text description — and, optionally, one or more screenshots — to the
 * `submitFeedback` mutation, which files a Linear issue (with the submitter's
 * email / customer / page URL auto-attached, and each screenshot embedded).
 *
 * Screenshots ride the house two-phase upload: each is PUT straight to S3 via a
 * presigned URL (getFeedbackImageUploadUrl), then its S3 key is handed to
 * submitFeedback. Users can add images with the picker OR by pasting a
 * screenshot straight from the clipboard.
 *
 * Graceful degradation: if feedback delivery is unavailable the mutation
 * returns success:false and we show an inline "reach out to Atomic directly"
 * fallback WITHOUT clearing the user's text or attachments. If an image upload
 * fails it is simply dropped from the report — a failed screenshot never blocks
 * the text from being sent.
 */
export function FeedbackModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { addMessage } = useSystemMessages();
  const [description, setDescription] = useState("");
  const [failed, setFailed] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const nextIdRef = useRef(1);
  // Mirror of `attachments` for the unmount cleanup, so it can revoke the latest
  // object URLs without depending on them (and re-registering the effect).
  const attachmentsRef = useRef<Attachment[]>([]);
  const [submitFeedback, { loading: submitting }] = useMutation(SubmitFeedbackDocument);

  const busy = submitting || uploading;

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Revoke any outstanding object URLs when the modal unmounts so previews don't
  // leak. (Per-attachment revokes happen in removeAttachment()/reset().)
  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    };
  }, []);

  const reset = () => {
    setDescription("");
    setFailed(false);
    setAttachError(null);
    attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    setAttachments([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  // Validate + add files from either the picker or a clipboard paste. Rejections
  // surface an inline message rather than silently dropping the file. Object URLs
  // are created here (not inside a state updater) so they aren't double-created.
  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    let error: string | null = null;
    let count = attachments.length;
    const accepted: Attachment[] = [];
    for (const file of files) {
      if (count >= MAX_IMAGES) {
        error = `You can attach up to ${MAX_IMAGES} images.`;
        break;
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        error = "Only PNG, JPEG, GIF, or WebP images can be attached.";
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        error = "Images must be 10 MB or smaller.";
        continue;
      }
      accepted.push({
        id: nextIdRef.current++,
        file,
        previewUrl: URL.createObjectURL(file),
      });
      count++;
    }
    if (accepted.length > 0) setAttachments((cur) => [...cur, ...accepted]);
    setAttachError(error);
  };

  const removeAttachment = (id: number) => {
    const target = attachments.find((a) => a.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    setAttachments((cur) => cur.filter((a) => a.id !== id));
  };

  // PUT one file to S3 via a presigned URL and return its S3 key, or null on
  // failure (one silent retry on the PUT before giving up). A null just drops
  // this screenshot from the report — it never blocks the text send.
  const uploadOne = async (file: File): Promise<string | null> => {
    try {
      const urlRes = await apolloClient.query({
        query: GetFeedbackImageUploadUrlDocument,
        variables: { contentType: file.type },
        fetchPolicy: "no-cache",
      });
      const slot = urlRes.data?.getFeedbackImageUploadUrl;
      if (!slot?.uploadUrl || !slot?.s3Key) return null;

      const put = async () =>
        fetch(slot.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
      let putRes = await put();
      if (!putRes.ok) putRes = await put(); // one silent retry
      if (!putRes.ok) return null;
      return slot.s3Key;
    } catch {
      return null;
    }
  };

  const handleSubmit = async () => {
    const text = description.trim();
    if (!text || busy) return;
    setFailed(false);

    // Phase 1: stage any screenshots in S3. Failures degrade to text-only.
    let imageKeys: string[] = [];
    if (attachments.length > 0) {
      setUploading(true);
      try {
        const keys = await Promise.all(attachments.map((a) => uploadOne(a.file)));
        imageKeys = keys.filter((k): k is string => !!k);
      } finally {
        setUploading(false);
      }
    }

    // Phase 2: file the report (with whatever images made it up).
    try {
      const result = await submitFeedback({
        variables: {
          description: text,
          pageUrl: window.location.href,
          imageKeys: imageKeys.length > 0 ? imageKeys : null,
        },
      });
      if (result.data?.submitFeedback?.success) {
        addMessage({
          type: "success",
          title: "Thanks — your feedback was sent",
          autoDismiss: true,
          dismissAfterMs: 4000,
        });
        close();
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent
        onPaste={(e) => {
          const imageFiles = Array.from(e.clipboardData?.files ?? []).filter((f) =>
            f.type.startsWith("image/")
          );
          if (imageFiles.length > 0) {
            e.preventDefault();
            addFiles(imageFiles);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what's working, what's not, or what you'd like to see — we'll
            get it to the Atomic team. Your email and the current page are
            attached automatically.
          </DialogDescription>
        </DialogHeader>

        <label css={s.label}>
          <span>Your feedback</span>
          <Textarea
            css={css`
              resize: vertical;
            `}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder="e.g. The shipments chart shows no data after I change the date range."
            autoFocus
          />
        </label>

        <div css={s.attachBlock}>
          <div css={s.attachRow}>
            <Button
              variant="tertiaryOutline"
              size="sm"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy || attachments.length >= MAX_IMAGES}
            >
              Attach image
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(",")}
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />

          {attachments.length > 0 && (
            <div css={s.thumbs}>
              {attachments.map((a) => (
                <div key={a.id} css={s.thumb}>
                  <img src={a.previewUrl} alt={a.file.name} css={s.thumbImg} />
                  <button
                    type="button"
                    css={s.remove}
                    aria-label={`Remove ${a.file.name}`}
                    onClick={() => removeAttachment(a.id)}
                    disabled={busy}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {attachError && <div css={s.attachErrorText}>{attachError}</div>}
        </div>

        {failed && (
          <div css={s.failure}>
            Feedback is temporarily unavailable. Please reach out to the Atomic
            team directly so we can help right away.
          </div>
        )}

        <DialogFooter>
          <Button variant="tertiaryOutline" size="sm" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={description.trim().length === 0 || busy}
          >
            {uploading ? "Uploading…" : submitting ? "Sending…" : "Send feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const s = {
  label: css`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
  `,
  attachBlock: css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `,
  attachRow: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  thumbs: css`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  `,
  thumb: css`
    position: relative;
    width: 4rem;
    height: 4rem;
    border: 1px solid ${theme.colors.border};
    border-radius: 0.375rem;
    overflow: hidden;
  `,
  thumbImg: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  `,
  remove: css`
    position: absolute;
    top: 0;
    right: 0;
    width: 1.125rem;
    height: 1.125rem;
    line-height: 1rem;
    padding: 0;
    border: none;
    border-bottom-left-radius: 0.375rem;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 0.875rem;
    cursor: pointer;

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  `,
  attachErrorText: css`
    color: ${theme.colors.statusErrorForeground};
    font-size: 0.8125rem;
  `,
  failure: css`
    background: ${theme.colors.statusErrorSoft};
    color: ${theme.colors.statusErrorForeground};
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
  `,
};
